import {
  parsePriceChangeMoney,
  type PriceChangeLine,
} from "./priceChangeSnapshot";

export const PRICE_CHANGE_CSV_HEADERS = [
  "code",
  "name",
  "kind",
  "group",
  "roof_style",
  "current_wholesale",
  "current_msrp",
  "suggested_wholesale",
  "suggested_msrp",
  "new_wholesale",
  "new_msrp",
  "new_wholesale_confirmed",
  "new_msrp_confirmed",
] as const;

function moneyCell(value: number | null): string {
  return value == null ? "" : String(value);
}

function flagCell(confirmed: boolean, price: number | null): string {
  return confirmed && price != null ? "yes" : "";
}

function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function priceChangeLinesToCsv(lines: readonly PriceChangeLine[]): string {
  const rows = [PRICE_CHANGE_CSV_HEADERS.join(",")];
  for (const line of lines) {
    rows.push(
      [
        line.code,
        line.name,
        line.kind,
        line.group_name,
        line.roof_style,
        moneyCell(line.current_wholesale_usd),
        moneyCell(line.current_msrp_usd),
        moneyCell(line.suggested_wholesale_usd),
        moneyCell(line.suggested_msrp_usd),
        moneyCell(line.new_wholesale_usd),
        moneyCell(line.new_msrp_usd),
        flagCell(line.new_wholesale_confirmed === true, line.new_wholesale_usd),
        flagCell(line.new_msrp_confirmed === true, line.new_msrp_usd),
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return `${rows.join("\n")}\n`;
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const src = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
      continue;
    }
    if (ch === '"') {
      quoted = true;
      continue;
    }
    if (ch === ",") {
      row.push(cell);
      cell = "";
      continue;
    }
    if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
      continue;
    }
    cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim())) rows.push(row);
  return rows;
}

const CONFIRMED_YES = new Set(["yes", "y", "true", "1", "x"]);
const CONFIRMED_NO = new Set(["no", "n", "false", "0", ""]);

function parseConfirmedCell(raw: string): { ok: true; value: boolean } | { ok: false; error: string } {
  const text = raw.trim().toLowerCase();
  if (CONFIRMED_YES.has(text)) return { ok: true, value: true };
  if (CONFIRMED_NO.has(text)) return { ok: true, value: false };
  return { ok: false, error: "Confirmation must be yes or blank." };
}

export type PriceChangeImportRow = {
  code: string;
  new_wholesale_usd?: number | null;
  new_msrp_usd?: number | null;
  new_wholesale_confirmed?: boolean;
  new_msrp_confirmed?: boolean;
};

export function parsePriceChangeCsv(
  text: string,
): { ok: true; rows: PriceChangeImportRow[] } | { ok: false; error: string } {
  const table = parseCsvRows(text);
  if (!table.length) return { ok: false, error: "The file is empty." };
  const header = table[0].map((h) => h.trim().toLowerCase());
  const codeIdx = header.indexOf("code");
  if (codeIdx < 0) return { ok: false, error: "CSV needs a code column." };
  const wsIdx = header.indexOf("new_wholesale");
  const msrpIdx = header.indexOf("new_msrp");
  const wsOkIdx = header.indexOf("new_wholesale_confirmed");
  const msrpOkIdx = header.indexOf("new_msrp_confirmed");
  const rows: PriceChangeImportRow[] = [];
  for (const cells of table.slice(1)) {
    const code = (cells[codeIdx] ?? "").trim();
    if (!code) continue;
    const row: PriceChangeImportRow = { code };
    if (wsIdx >= 0) {
      const parsed = parsePriceChangeMoney(cells[wsIdx] ?? "");
      if (!parsed.ok) return { ok: false, error: `${code} new wholesale: ${parsed.error}` };
      if (parsed.value != null) row.new_wholesale_usd = parsed.value;
    }
    if (msrpIdx >= 0) {
      const parsed = parsePriceChangeMoney(cells[msrpIdx] ?? "");
      if (!parsed.ok) return { ok: false, error: `${code} new MSRP: ${parsed.error}` };
      if (parsed.value != null) row.new_msrp_usd = parsed.value;
    }
    if (wsOkIdx >= 0) {
      const parsed = parseConfirmedCell(cells[wsOkIdx] ?? "");
      if (!parsed.ok) return { ok: false, error: `${code} wholesale confirmation: ${parsed.error}` };
      row.new_wholesale_confirmed = parsed.value;
    }
    if (msrpOkIdx >= 0) {
      const parsed = parseConfirmedCell(cells[msrpOkIdx] ?? "");
      if (!parsed.ok) return { ok: false, error: `${code} MSRP confirmation: ${parsed.error}` };
      row.new_msrp_confirmed = parsed.value;
    }
    rows.push(row);
  }
  return { ok: true, rows };
}

function readImportMoney(record: Record<string, unknown>, keys: string[]): number | null | undefined {
  for (const key of keys) {
    if (!(key in record)) continue;
    const parsed = parsePriceChangeMoney(record[key]);
    if (!parsed.ok) return undefined;
    return parsed.value;
  }
  return undefined;
}

function readImportFlag(
  record: Record<string, unknown>,
  keys: string[],
): { ok: true; value: boolean | undefined } | { ok: false; error: string } {
  for (const key of keys) {
    if (!(key in record)) continue;
    const value = record[key];
    if (value == null || value === "") return { ok: true, value: false };
    if (value === true) return { ok: true, value: true };
    if (value === false) return { ok: true, value: false };
    if (typeof value === "number" || typeof value === "string") {
      const parsed = parseConfirmedCell(String(value));
      if (!parsed.ok) return parsed;
      return { ok: true, value: parsed.value };
    }
    return { ok: false, error: "Confirmation must be yes or blank." };
  }
  return { ok: true, value: undefined };
}

export function parsePriceChangeJson(
  text: string,
): { ok: true; rows: PriceChangeImportRow[]; name?: string; notes?: string } | { ok: false; error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: "That file is not valid JSON." };
  }
  const root = Array.isArray(parsed)
    ? { lines: parsed }
    : parsed && typeof parsed === "object"
      ? (parsed as Record<string, unknown>)
      : null;
  if (!root || !Array.isArray(root.lines)) {
    return { ok: false, error: "JSON needs a lines array, or a bare array of rows." };
  }
  const rows: PriceChangeImportRow[] = [];
  for (const item of root.lines) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const code = String(record.code ?? "").trim();
    if (!code) continue;
    const wholesale = readImportMoney(record, ["new_wholesale_usd", "new_wholesale"]);
    const msrp = readImportMoney(record, ["new_msrp_usd", "new_msrp"]);
    if (wholesale === undefined && ("new_wholesale_usd" in record || "new_wholesale" in record)) {
      return { ok: false, error: `${code} new wholesale is not a dollar amount.` };
    }
    if (msrp === undefined && ("new_msrp_usd" in record || "new_msrp" in record)) {
      return { ok: false, error: `${code} new MSRP is not a dollar amount.` };
    }
    const wholesaleConfirmed = readImportFlag(record, ["new_wholesale_confirmed", "wholesale_confirmed"]);
    if (!wholesaleConfirmed.ok) return { ok: false, error: `${code} wholesale confirmation: ${wholesaleConfirmed.error}` };
    const msrpConfirmed = readImportFlag(record, ["new_msrp_confirmed", "msrp_confirmed"]);
    if (!msrpConfirmed.ok) return { ok: false, error: `${code} MSRP confirmation: ${msrpConfirmed.error}` };
    const row: PriceChangeImportRow = { code };
    if (wholesale != null) row.new_wholesale_usd = wholesale;
    if (msrp != null) row.new_msrp_usd = msrp;
    if (wholesaleConfirmed.value !== undefined) row.new_wholesale_confirmed = wholesaleConfirmed.value;
    if (msrpConfirmed.value !== undefined) row.new_msrp_confirmed = msrpConfirmed.value;
    rows.push(row);
  }
  const name = typeof root.name === "string" ? root.name : undefined;
  const notes = typeof root.notes === "string" ? root.notes : undefined;
  return { ok: true, rows, name, notes };
}

function applySide(
  line: PriceChangeLine,
  price: number | null | undefined,
  priceKey: "new_wholesale_usd" | "new_msrp_usd",
  confirmed: boolean | undefined,
  confirmedKey: "new_wholesale_confirmed" | "new_msrp_confirmed",
): boolean {
  let changed = false;
  if (price != null && price !== line[priceKey]) {
    line[priceKey] = price;
    changed = true;
    if (confirmed === undefined) line[confirmedKey] = false;
  }
  if (confirmed !== undefined) {
    const next = confirmed && line[priceKey] != null;
    if (line[confirmedKey] !== next) {
      line[confirmedKey] = next;
      changed = true;
    }
  } else if (line[priceKey] == null && line[confirmedKey]) {
    line[confirmedKey] = false;
    changed = true;
  }
  return changed;
}

export function applyPriceChangeImport(
  lines: readonly PriceChangeLine[],
  rows: readonly PriceChangeImportRow[],
): { lines: PriceChangeLine[]; updated: number; unknown: string[] } {
  const byCode = new Map(lines.map((line) => [line.code.trim().toLowerCase(), line]));
  const next = lines.map((line) => ({
    ...line,
    new_wholesale_confirmed: line.new_wholesale_confirmed === true,
    new_msrp_confirmed: line.new_msrp_confirmed === true,
  }));
  const index = new Map(next.map((line, i) => [line.code.trim().toLowerCase(), i]));
  let updated = 0;
  const unknown: string[] = [];
  for (const row of rows) {
    const key = row.code.trim().toLowerCase();
    const at = index.get(key);
    if (at == null || !byCode.has(key)) {
      unknown.push(row.code);
      continue;
    }
    const line = next[at];
    const wholesaleChanged = applySide(
      line,
      row.new_wholesale_usd,
      "new_wholesale_usd",
      row.new_wholesale_confirmed,
      "new_wholesale_confirmed",
    );
    const msrpChanged = applySide(
      line,
      row.new_msrp_usd,
      "new_msrp_usd",
      row.new_msrp_confirmed,
      "new_msrp_confirmed",
    );
    if (wholesaleChanged || msrpChanged) updated += 1;
  }
  return { lines: next, updated, unknown };
}
